import { useState, useEffect, useCallback } from 'react';
import { novelApi } from '../../services/api/novels';
import { chapterApi } from '../../services/api/chapters';
import type { Novel, Chapter } from '../../services/types';

interface NovelChapterSelectorProps {
  selectedNovelId: string;
  selectedChapterIds: string[];
  onNovelChange: (novelId: string) => void;
  onChapterIdsChange: (ids: string[]) => void;
}

export function NovelChapterSelector({
  selectedNovelId,
  selectedChapterIds,
  onNovelChange,
  onChapterIdsChange,
}: NovelChapterSelectorProps) {
  const [novels, setNovels] = useState<Novel[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [isLoadingNovels, setIsLoadingNovels] = useState(true);
  const [isLoadingChapters, setIsLoadingChapters] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadNovels = useCallback(async () => {
    try {
      setIsLoadingNovels(true);
      const data = await novelApi.list();
      setNovels(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load novels');
    } finally {
      setIsLoadingNovels(false);
    }
  }, []);

  const loadChapters = useCallback(async (novelId: string) => {
    if (!novelId) {
      setChapters([]);
      return;
    }
    try {
      setIsLoadingChapters(true);
      const response = await chapterApi.list(novelId, 1, 500);
      setChapters(response.chapters);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load chapters');
    } finally {
      setIsLoadingChapters(false);
    }
  }, []);

  useEffect(() => {
    loadNovels();
  }, [loadNovels]);

  useEffect(() => {
    loadChapters(selectedNovelId);
  }, [selectedNovelId, loadChapters]);

  return (
    <div className="card p-4 space-y-4">
      {error && (
        <div className="text-sm text-danger-600 dark:text-danger-400">{error}</div>
      )}
      <div>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
          小說
        </label>
        {isLoadingNovels ? (
          <div className="flex items-center gap-2 text-neutral-500">
            <span className="w-4 h-4 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
            載入小說中...
          </div>
        ) : (
          <select
            value={selectedNovelId}
            onChange={(e) => onNovelChange(e.target.value)}
            className="select w-full"
          >
            <option value="">-- 不選擇小說 --</option>
            {novels.map((novel) => (
              <option key={novel.id} value={novel.id}>
                {novel.title} ({novel.author})
              </option>
            ))}
          </select>
        )}
      </div>

      {selectedNovelId && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              章節 (可多選)
            </label>
            {selectedChapterIds.length > 0 && (
              <span className="text-xs text-primary-600 dark:text-primary-400">
                已選 {selectedChapterIds.length} 章
              </span>
            )}
          </div>
          {isLoadingChapters ? (
            <div className="flex items-center gap-2 text-neutral-500">
              <span className="w-4 h-4 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
              載入章節中...
            </div>
          ) : chapters.length === 0 ? (
            <p className="text-sm text-warning-600 dark:text-warning-400">
              這本小說沒有章節
            </p>
          ) : (
            <div className="space-y-2">
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => onChapterIdsChange(chapters.map((c) => c.id))}
                  className="btn btn-ghost btn-sm text-xs"
                >
                  全選
                </button>
                <button
                  type="button"
                  onClick={() => onChapterIdsChange([])}
                  className="btn btn-ghost btn-sm text-xs"
                >
                  取消全選
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto border border-neutral-200 dark:border-neutral-700 rounded-button p-2 space-y-1">
                {chapters.map((chapter) => (
                  <label
                    key={chapter.id}
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedChapterIds.includes(chapter.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onChapterIdsChange([...selectedChapterIds, chapter.id]);
                        } else {
                          onChapterIdsChange(selectedChapterIds.filter((id) => id !== chapter.id));
                        }
                      }}
                      className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-neutral-700 dark:text-neutral-300">
                      {chapter.chapter_index}. {chapter.title}
                    </span>
                    {chapter.word_count && (
                      <span className="text-xs text-neutral-400 ml-auto">
                        {chapter.word_count.toLocaleString()} 字
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
